import { useCallback, useMemo } from 'react'
import uniqBy from 'lodash/uniqBy'

import { ImageMetadata } from '@makyoapp/makyo-sdk/models/Files'
import {
  ColumnConditionValue,
  IActionRepeaterValue,
  IActionType,
  parseUrlToMakyoImageSize,
  TypographyGroupValue,
} from '@makyoapp/makyo-common'
import {
  CartItem,
  ColorVariant,
  ColumnTypes,
  CornerRadius,
  HoverEffectVariant,
  HoverIntensity,
  Ratio,
  RawSpacingInput,
  ShoppingCart,
  Skeleton,
  SkeletonGroup,
  TypographyValue,
} from '@makyoapp/makyo-tw-ui'

import { IDBTable } from '@/core/db/table'
import { CoreComponentAction, CoreComponentInfos, CoreComponentTableData } from '@/core/models/app-components/types'
import { INumberColumnSettings, NumberCell } from '@/core/models/columns/number'
import { cn } from '@/lib/cn'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useCompTableData } from '@/hooks/__project__/useCompTableData'
import { ComponentSettingKey } from '@/shared/builder/types'
import { getSafely } from '@/shared/safely'
import { CompOverlayDevmode } from '@/components/__project__/shared/CompOverlayDevmode'

import { CompHeadline } from '../../shared/CompHeadline'
import { convertRatioFormat } from '../../utils'

const getPriceColumnSettings = (table?: IDBTable, columnId?: string): INumberColumnSettings | undefined => {
  const column = columnId ? table?.columnsMap.get(columnId) : undefined
  if (!column) return

  if (column._Type === ColumnTypes.Lookup) {
    return column._ResolvedColumnSettings as INumberColumnSettings
  }

  return column._Settings as INumberColumnSettings
}

export interface CompShoppingCartWrapperCoreProps {
  infos: CoreComponentInfos
  action: CoreComponentAction
  tableData: CoreComponentTableData
}

export function CompShoppingCartWrapperCore({
  infos: { component, containerClient, settings, detailTableId, detailRowIndex, rowId: detailRowId, currentParentBg },
  action: { simulateAction },
  tableData: { dbTable: table },
}: CompShoppingCartWrapperCoreProps) {
  const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()

  // ========================= data =========================
  // data source
  const tableId = settings[ComponentSettingKey.ShoppingCartDataSource] as string

  // product columns
  const colName = settings[ComponentSettingKey.ShoppingCartDataProductName] as string
  const colImage = settings[ComponentSettingKey.ShoppingCartDataProductImage] as string
  const colInfo = settings[ComponentSettingKey.ShoppingCartDataProductInfo] as string
  const colPrice = settings[ComponentSettingKey.ShoppingCartDataProductPrice] as string
  const colQuantity = settings[ComponentSettingKey.ShoppingCartDataProductQuantity] as string
  const colStockStatus = settings[ComponentSettingKey.ShoppingCartDataProductStockStatus] as string

  // price column settings (for formatting)
  const priceColumnSettings = getPriceColumnSettings(table, colPrice)

  // filter
  const filterSetting = settings[ComponentSettingKey.ShoppingCartDataFilter] as ColumnConditionValue | undefined
  const sortBy = settings[ComponentSettingKey.ShoppingCartDataSortBy] as string
  const sortType = settings[ComponentSettingKey.ShoppingCartDataSortType] as string

  // get all rows
  const [{ rows: allRows, allRowsCount }, isLoading] = useCompTableData({
    table,
    componentId: component._id,
    filterSetting,
    sortSetting: { sortType, sortBy },
  })

  // map rows into cart items
  const cartItems: CartItem[] = useMemo(() => {
    return uniqBy(
      allRows.map((row) => {
        return {
          id: row._id as any,
          name: row[colName]?.toString(),
          image: parseUrlToMakyoImageSize(row[colImage]?.toString() || '', ImageMetadata.S),
          info: row[colInfo]?.toString(),
          price: row[colPrice]?.value as number,
          quantity: row[colQuantity]?.value as number,
          inStock: true,
          isFavorite: false,
        }
      }),
      'id'
    )
    //
  }, [allRows, colImage, colInfo, colName, colPrice, colQuantity])

  // ========================= design =========================

  // base
  const width = getSafely(settings, [ComponentSettingKey.ShoppingCartDesignWidth], ['100%'])[0]
  const alignment = settings[ComponentSettingKey.ShoppingCartDesignCompHAlign] ?? 'parent'
  const margin = settings[ComponentSettingKey.ShoppingCartDesignMargin] as RawSpacingInput

  // styles
  const bgColor = settings[ComponentSettingKey.ShoppingCartDesignBgColor] ?? 'primary-80'
  const summaryBgColor = settings[ComponentSettingKey.ShoppingCartDesignSummaryBgColor] ?? 'primary-90'
  const checkoutButtonColor = settings[ComponentSettingKey.ShoppingCartDesignPrimaryButtonColor] ?? 'primary-20'
  const continueButtonColor = settings[ComponentSettingKey.ShoppingCartDesignSecondaryButtonColor] ?? 'primary-90'
  const cornerRadius = settings[ComponentSettingKey.ShoppingCartDesignCornerRadius] ?? 'medium'
  const imageWidth = getSafely(settings, [ComponentSettingKey.ShoppingCartDesignImageWidth], ['80px'])[0]
  const imageRatio = (convertRatioFormat(settings?.[ComponentSettingKey.ShoppingCartDesignImageRatio]) ??
    '1/1') as Ratio
  const imageRadius = (settings?.[ComponentSettingKey.ShoppingCartDesignImageRadius] as CornerRadius) ?? 'medium'
  const buttonRadius = (settings?.[ComponentSettingKey.ShoppingCartDesignButtonRadius] as CornerRadius) ?? 'medium'

  const hoverEffect = settings[ComponentSettingKey.ShoppingCartDesignHoverEffect] as HoverEffectVariant
  const hoverIntensity = 'md' as HoverIntensity

  // typography
  const typoSettings = settings[ComponentSettingKey.ShoppingCartDesignTypography] as TypographyGroupValue
  const productNameTypography =
    typoSettings[ComponentSettingKey.ShoppingCartDesignTitleTypography] ??
    ({ typography: 'body-medium' } as TypographyValue)
  const productInfoTypography =
    typoSettings[ComponentSettingKey.ShoppingCartDesignInfoTypography] ??
    ({ typography: 'body-extra-small' } as TypographyValue)

  // ========================= action =========================
  const onItemClickAction = settings[ComponentSettingKey.ShoppingCartActionItemClick] as IActionRepeaterValue[]
  const quantityIncrementAction = settings[ComponentSettingKey.ShoppingCartActionQtyIncrement] as IActionRepeaterValue[]
  const quantityDecrementAction = settings[ComponentSettingKey.ShoppingCartActionQtyDecrement] as IActionRepeaterValue[]
  const removeItemAction = settings[ComponentSettingKey.ShoppingCartActionRemoveItem] as IActionRepeaterValue[]
  const onProceedAction = settings[ComponentSettingKey.ShoppingCartActionOnProceed] as IActionRepeaterValue[]
  const onBackAction = settings[ComponentSettingKey.ShoppingCartActionOnBack] as IActionRepeaterValue[]

  // action: item click
  const handleItemClick = useCallback(
    (rowId: string) => {
      simulateAction({
        actions: onItemClickAction,
        tableId,
        rowId,
      })
    },
    [simulateAction, onItemClickAction, tableId]
  )

  // action: remove item
  const handleRemoveItem = useCallback(
    (rowId: string) => {
      simulateAction({
        actions: removeItemAction,
        tableId,
        rowId,
      })
    },
    [simulateAction, removeItemAction, tableId]
  )

  // action: update quantity
  const handleUpdateQuantity = useCallback(
    async (rowId: string, type: 'increment' | 'decrement') => {
      const currentQuantity = cartItems.find((item) => item.id === rowId)?.quantity ?? 0

      // !harcoded action: remove item if users try to decrement quantity to 0 or already below 0
      if (type === 'decrement' && currentQuantity <= 1) {
        await simulateAction({
          actions: [
            {
              id: 'remove-item-action',
              type: IActionType.DeleteRow,
              settings: {},
            },
          ],
          tableId,
          rowId,
        })
        return
      }

      await simulateAction({
        actions: type === 'increment' ? quantityIncrementAction : quantityDecrementAction,
        tableId,
        rowId,
        external: {
          detailsPage: {
            detailTableId,
            detailRowIndex,
            detailRowId,
          },
        },
      })
    },
    [
      simulateAction,
      cartItems,
      detailRowIndex,
      detailTableId,
      quantityDecrementAction,
      quantityIncrementAction,
      tableId,
      detailRowId,
    ]
  )

  // action: proceed
  const handleProceed = useCallback(() => {
    simulateAction({
      actions: onProceedAction,
    })
  }, [simulateAction, onProceedAction])

  // action: back
  const handleBack = useCallback(() => {
    simulateAction({
      actions: onBackAction,
    })
  }, [simulateAction, onBackAction])

  // ========================= behavior =========================
  const allowQuantityChange = settings[ComponentSettingKey.ShoppingCartBehaviorAllowQtyChange] ?? true
  const allowRemoveItems = settings[ComponentSettingKey.ShoppingCartBehaviorAllowRemoveItem] ?? true

  // ========================= component title / headline =========================
  const compTitle = (settings[ComponentSettingKey.ShoppingCartDataCompTitle] as any[]) ?? []
  const compTitleMargin = settings[ComponentSettingKey.ShoppingCartDesignCompTitleMargin]
  const compTitleTypo = typoSettings[ComponentSettingKey.ShoppingCartDesignCompTitleTypography]
  const compTitleAlign = settings[ComponentSettingKey.ShoppingCartDesignCompTitleAlign]

  // ========================= render =========================

  if (isLoading) {
    return <ShoppingCartSkeleton backgroundColor={currentParentBg!} />
  }

  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  const shoppingCartComponent = (
    <ShoppingCart
      // base
      id={component._id}
      width={width}
      alignment={alignment}
      margin={margin}
      // Promo code
      promoCode={''}
      promoApplied={false}
      appliedPromos={[]}
      allowPromoCodes={false}
      onPromoCodeChange={() => {}}
      onApplyPromoCode={() => {}}
      onRemovePromo={() => {}}
      // Shipping options
      shippingOption='free'
      showShippingOptions={false}
      onShippingOptionChange={() => {}}
      onToggleShippingOptions={() => {}}
      // Customization
      componentTitle={
        <CompHeadline
          containerClient={containerClient}
          detailTableId={detailTableId}
          detailRowIndex={detailRowIndex}
          compTitleAlign={compTitleAlign}
          headline={compTitle}
          headlineMargin={compTitleMargin}
          headlineTypo={compTitleTypo}
          backgroundColor={currentParentBg}
          colorType='adaptive'
        />
      }
      allowQuantityChange={allowQuantityChange}
      allowRemoveItems={allowRemoveItems}
      allowFavorites={false}
      gapBetweenItems={16}
      elevation='sm'
      // Cart items and actions
      items={cartItems}
      onItemClick={handleItemClick}
      onCheckout={handleProceed}
      onContinueShopping={handleBack}
      onUpdateQuantity={handleUpdateQuantity}
      onRemoveItem={handleRemoveItem}
      // onToggleFavorite={() => {}}

      // Calculation
      showTax={false}
      formatCurrency={(value) => {
        if (!priceColumnSettings) {
          return value.toString()
        }
        return new NumberCell(value, priceColumnSettings).toString()
      }}
      // taxValue={0.08}
      // taxLabel={''}
      // taxDescription={''}

      // Styles
      backgroundColor={bgColor}
      summaryBgColor={summaryBgColor}
      primaryButtonColor={checkoutButtonColor}
      secondaryButtonColor={continueButtonColor}
      cornerRadius={cornerRadius}
      imageWidth={imageWidth}
      imageRatio={imageRatio}
      imageRadius={imageRadius}
      buttonRadius={buttonRadius}
      // Typography
      productNameTypography={productNameTypography}
      productInfoTypography={productInfoTypography}
      hoverEffect={hoverEffect as HoverEffectVariant}
      hoverIntensity={hoverIntensity as HoverIntensity}
    />
  )

  if (isDevelopmentMode) {
    return (
      <div className='relative flex flex-col w-full border border-dashed' style={{ borderColor: '#333' }}>
        {shoppingCartComponent}
        <CompOverlayDevmode handleClick={handleClick} />
      </div>
    )
  }

  return shoppingCartComponent
}

const ShoppingCartSkeleton = ({ backgroundColor }: { backgroundColor: ColorVariant }) => {
  return (
    <SkeletonGroup backgroundColor={backgroundColor}>
      <div className='w-full p-4'>
        <div className='pt-6 w-full flex flex-col gap-6'>
          {[...new Array(4)].map((_, i) => (
            <div
              key={i}
              className={cn(`w-full flex gap-4 border border-on-${backgroundColor}/50 rounded-lg px-4 py-5`)}
            >
              <Skeleton className='w-20 h-20 aspect-square rounded-lg shrink-0' />
              <div className='flex-1'>
                <div className='flex items-center justify-between'>
                  <Skeleton className='w-36 h-3 rounded-lg' />
                  <Skeleton className='w-16 h-3 rounded-lg' />
                </div>
                <Skeleton className='mt-4 w-18 h-4 rounded-lg' />
                <div className='mt-4 flex items-center justify-between'>
                  <Skeleton className='w-24 h-6 rounded-lg' />
                  <Skeleton className='w-6 h-6 rounded-lg' />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className='mt-8 flex items-center justify-between'>
          <Skeleton className='w-24 h-4 rounded-lg' />
          <Skeleton className='w-24 h-4 rounded-lg' />
        </div>
        <hr className={cn(`my-4 border-on-${backgroundColor}/50`)} />
        <div className='flex items-center justify-between'>
          <Skeleton className='w-24 h-4 rounded-lg' />
          <Skeleton className='w-24 h-4 rounded-lg' />
        </div>

        <div className='mt-6 space-y-2'>
          <Skeleton className='w-full h-12 rounded-lg' />
          <Skeleton className='w-full h-12 rounded-lg' />
        </div>
      </div>
    </SkeletonGroup>
  )
}
